import type { ApiPaths } from '@aztec-realms/api-client';
import type { DiscoveryMarkerView } from '@aztec-realms/shared-types';

import { mapDiscovery } from './api-vertical-slice';
import { browserApiClient, requiredApiData } from './browser-api';

export const JOURNEY_SESSION_KEY = 'aztec-realms:journey-session-id';

type JourneyStartOperation = NonNullable<ApiPaths['/api/v1/explore/journeys']['post']>;
type JourneyStartResponse = JourneyStartOperation['responses'][200]['content']['application/json'];
type JourneyResponseCore = Pick<
  JourneyStartResponse,
  | 'id'
  | 'nonce'
  | 'next_sequence_no'
  | 'status'
  | 'started_at'
  | 'passive_mode'
  | 'movement_mode'
  | 'interaction_locked'
  | 'stationary_confirmed'
  | 'fraud_score'
  | 'reward_decision'
  | 'sample_retention_until'
  | 'queued_discoveries'
>;
type JourneySamplesOperation = NonNullable<
  ApiPaths['/api/v1/explore/journeys/{journey_id}/samples']['post']
>;
export type JourneySampleInput =
  JourneySamplesOperation['requestBody']['content']['application/json']['samples'][number];

export interface JourneyAuthoritySnapshot {
  id: string;
  startedAt?: string;
  nonce: string;
  nextSequenceNo: number;
  status: string;
  passiveMode: boolean;
  movementMode: 'stationary' | 'walking' | 'cycling' | 'transit';
  interactionLocked: boolean;
  stationaryConfirmed: boolean;
  fraudScore: number;
  rewardDecision: 'accepted' | 'held' | 'rejected';
  sampleRetentionUntil?: string;
  queuedDiscoveryCount: number;
  queuedDiscoveries: readonly DiscoveryMarkerView[];
}

function mapJourney(response: JourneyResponseCore): JourneyAuthoritySnapshot {
  return {
    id: response.id,
    startedAt: response.started_at,
    nonce: response.nonce,
    nextSequenceNo: response.next_sequence_no,
    status: response.status,
    passiveMode: response.passive_mode,
    movementMode: response.movement_mode,
    interactionLocked: response.interaction_locked,
    stationaryConfirmed: response.stationary_confirmed,
    fraudScore: response.fraud_score,
    rewardDecision: response.reward_decision,
    sampleRetentionUntil: response.sample_retention_until ?? undefined,
    queuedDiscoveryCount: response.queued_discoveries.length,
    queuedDiscoveries: response.queued_discoveries.map(mapDiscovery),
  };
}

export function persistedJourneyId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.sessionStorage.getItem(JOURNEY_SESSION_KEY) ?? undefined;
}

export async function startForegroundJourney(
  mode: 'foreground' | 'fixture' = 'foreground',
): Promise<JourneyAuthoritySnapshot> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/explore/journeys', {
    body: { mode },
  });
  const journey = requiredApiData(
    response.data,
    response.error,
    'The foreground Journey could not start.',
  );
  window.sessionStorage.setItem(JOURNEY_SESSION_KEY, journey.id);
  return mapJourney(journey);
}

export async function submitForegroundJourneySamples(
  journeyId: string,
  nonce: string,
  sequenceNo: number,
  samples: readonly JourneySampleInput[],
  focusDiscoveryId?: string,
  signal?: AbortSignal,
): Promise<JourneyAuthoritySnapshot> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/explore/journeys/{journey_id}/samples', {
    signal,
    params: {
      path: { journey_id: journeyId },
      header: { 'Idempotency-Key': `journey-${journeyId}-${sequenceNo}` },
    },
    body: {
      nonce,
      sequence_no: sequenceNo,
      samples: [...samples],
      focus_discovery_id: focusDiscoveryId,
    },
  });
  return mapJourney(
    requiredApiData(response.data, response.error, 'Journey samples could not be verified.'),
  );
}

export async function finishForegroundJourney(
  journeyId: string,
): Promise<JourneyAuthoritySnapshot> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/explore/journeys/{journey_id}/finish', {
    params: { path: { journey_id: journeyId } },
  });
  const journey = requiredApiData(
    response.data,
    response.error,
    'The Journey could not finish safely.',
  );
  if (persistedJourneyId() === journeyId) window.sessionStorage.removeItem(JOURNEY_SESSION_KEY);
  return mapJourney(journey);
}

export async function cancelForegroundJourney(
  journeyId: string,
  options: { keepalive?: boolean } = {},
): Promise<JourneyAuthoritySnapshot> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/explore/journeys/{journey_id}/cancel', {
    params: { path: { journey_id: journeyId } },
    keepalive: options.keepalive,
  });
  const journey = requiredApiData(
    response.data,
    response.error,
    'The Journey could not be cancelled safely.',
  );
  if (persistedJourneyId() === journeyId) window.sessionStorage.removeItem(JOURNEY_SESSION_KEY);
  return mapJourney(journey);
}
